import type { ChatTurn } from "./components/Assistant";

/**
 * Chat history lives in the browser, not on a server: the AgentCore runtime
 * already keeps its own memory of conversations, but that memory feeds the
 * model — nothing serves a transcript back to the UI. localStorage is what
 * lets an answer survive a reload and lets the user reopen an older thread.
 * It is per-browser and unscoped by login, which fits a single-analyst
 * dashboard; a shared machine would show one user another's questions.
 */
export interface StoredConversation {
  id: string;
  startedAt: string;
  updatedAt: string;
  turns: ChatTurn[];
}

/** What the history list renders — derived, never stored. */
export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
  turnCount: number;
}

const storageKey = "watchpoint.assistant.conversations";

/** Enough threads to scroll back through without hoarding evidence blobs. */
const conversationCap = 20;

function isStoredConversation(value: unknown): value is StoredConversation {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<StoredConversation>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.startedAt === "string" &&
    typeof candidate.updatedAt === "string" &&
    Array.isArray(candidate.turns)
  );
}

export function readStoredConversations(): StoredConversation[] {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isStoredConversation);
  } catch {
    // Corrupt JSON or storage denied: history is a convenience, never a
    // reason the assistant fails to load.
    return [];
  }
}

export function writeStoredConversations(
  conversations: StoredConversation[],
): void {
  // A thread's evidence (SQL, lineage) can be sizeable; if the quota rejects
  // the write, shed the oldest threads until it fits rather than losing all
  // history at once.
  let list = conversations;
  while (list.length > 0) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(list));
      return;
    } catch {
      list = list.slice(0, -1);
    }
  }
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Storage unavailable altogether; nothing to persist to.
  }
}

/**
 * Front-inserts or refreshes one conversation, newest first, capped. Returns
 * the input list unchanged when the front thread already holds these turns,
 * so a state effect can call this without looping.
 */
export function upsertConversation(
  conversations: StoredConversation[],
  id: string,
  turns: ChatTurn[],
  now: string,
): StoredConversation[] {
  if (turns.length === 0) return conversations;
  const existing = conversations.find((conversation) => conversation.id === id);
  if (existing && conversations[0] === existing && existing.turns === turns) {
    return conversations;
  }
  const updated: StoredConversation = {
    id,
    startedAt: existing?.startedAt ?? now,
    updatedAt: now,
    turns,
  };
  return [
    updated,
    ...conversations.filter((conversation) => conversation.id !== id),
  ].slice(0, conversationCap);
}

export function summarizeConversations(
  conversations: StoredConversation[],
): ConversationSummary[] {
  return conversations.map((conversation) => ({
    id: conversation.id,
    title: conversation.turns[0]?.question ?? "New conversation",
    updatedAt: conversation.updatedAt,
    turnCount: conversation.turns.length,
  }));
}
